import {
  Logo,
  Button,
  Canvas,
  PlusIcon,
  SceneContainer,
  ButtonContainer
} from './Overview.styles';

import Scene from './Scene';
import Content from './content';
import { Theme } from '@/theme';
import API from '@/services/api';
import type { Vector2 } from 'three';
import { connect } from 'react-redux';
import { useLoader } from '@CS/hooks';
import { Images } from '@/assets/images';

import EditButton from '@/components/EditButton';
import AvatarLabel from '@/components/AvatarLabel';
import { useRef, useState, useEffect } from 'react';
import useTranslation from '@CS/hooks/useTranslation';
import type { EntityData } from '@/services/api/types';
import { getCampaign } from '@/redux/campaign/selectors';
import type { CampaignState } from '@/redux/campaign/types';
import { WelcomePopup } from '@/components/Popups/WelcomePopup';
import type { GLTF } from 'three/examples/jsm/loaders/GLTFLoader';
import { type ISDK, type AvatarData } from '@tbwax_dev/mcd-family-sdk';
import { getUserEntityData, getUserState } from '@/redux/user/selectors';
import { AvatarCreatePopup } from '@/components/Popups/AvatarCreatePopup';
import type { UserState } from '@/redux/user/types';

type OverviewProps = {
  campaign: CampaignState;
  user: UserState;
  data: EntityData;
  sdk?: ISDK;
};

interface FamilyMember {
  memberData: AvatarData;
  avatar: GLTF;
}

const Overview = ({ sdk, user, data, campaign }: OverviewProps) => {
  const [hasAvatars, setHasAvatars] = useState<boolean | undefined>(void 0);
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);
  const [showAvatarCreate, setShowAvatarCreate] = useState(false);

  const [showWelcome, setShowWelcome] = useState(false);
  const [labels, setLabels] = useState<Vector2[]>([]);
  const canvas = useRef<HTMLCanvasElement>(null);
  const [loading, setLoading] = useState(true);

  const button = useTranslation(Content);
  const names = useRef<string[]>([]);
  const scene = useRef<Scene>(null);

  const loader = useLoader(loading, { color: Theme.color.orange });

  const createAvatar = () =>
    (location.href = `${(campaign.publicData as Record<string, string>).avatarCreator}`);

  const handleClosePopup = () => {
    if (showWelcome) {
      setShowWelcome(false);
      setShowAvatarCreate(true);
    } else if (showAvatarCreate) {
      setShowAvatarCreate(false);
      API.updateUser({ data: { popups: true } });
    }
  };

  const loadFamilyData = async (sdk: ISDK) => {
    try {
      const familyData = await sdk.getFamilyData();
      const { length } = familyData?.avatars ?? {};

      if (!length) {
        setHasAvatars(false);
        setLoading(false);
        return;
      }

      const member = familyData?.avatars;
      const avatars = await sdk.getFamilyAvatars();
      if (!avatars) return console.error('Avatars not loaded.');

      const membersData = avatars
        .map((avatar, a) => ({ avatar, memberData: member?.[a] }))
        .filter((item): item is FamilyMember => !!item.memberData);

      setFamilyMembers(membersData);
      setHasAvatars(true);
      setLoading(false);
    } catch (error) {
      setHasAvatars(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    const { length } = familyMembers;
    if (!length || !hasAvatars || !canvas.current || labels.length) return;

    scene.current = new Scene(canvas.current);

    familyMembers.forEach(({ avatar, memberData: { age, name } }, m) => {
      scene.current!.addAvatar(avatar, age, m, length);
      names.current.push(name);
    });

    setLabels(scene.current.labels);
  }, [canvas.current, familyMembers, hasAvatars]);

  useEffect(() => {
    if (!data) return;
    const { popups = false } = data ?? {};

    if (hasAvatars === false && !popups && !showWelcome && !showAvatarCreate) {
      setShowAvatarCreate(false);
      setShowWelcome(true);
    }
    //
    else if (popups || hasAvatars !== false) {
      setShowAvatarCreate(false);
      setShowWelcome(false);
    }
  }, [data, hasAvatars]);

  useEffect(() => {
    sdk && loadFamilyData(sdk);
  }, [sdk]);

  if (loading || !data)
    return (
      <SceneContainer>
        <Logo alt="Family Mode" src={user?.language === 'ar' ? Images.LogoAR : Images.LogoEN} />
        {loader}
      </SceneContainer>
    );

  if (!hasAvatars) {
    return (
      <>
        <SceneContainer>
          <Logo alt="Family Mode" src={user?.language === 'ar' ? Images.LogoAR : Images.LogoEN} />

          <ButtonContainer>
            <Button onClick={createAvatar}>
              <PlusIcon src={Images.PlusIcon} alt="Plus Icon" />
              {button}
            </Button>
          </ButtonContainer>
        </SceneContainer>

        {!(data?.popups ?? false) && (
          <>
            {showWelcome && <WelcomePopup onClose={handleClosePopup} />}
            {showAvatarCreate && (
              <AvatarCreatePopup onClose={handleClosePopup} />
            )}
          </>
        )}
      </>
    );
  }

  return (
    <SceneContainer>
      <Logo alt="Family Mode" src={user?.language === 'ar' ? Images.LogoAR : Images.LogoEN} />
      <Canvas ref={canvas} />

      {labels.map(({ x, y }, n) => (
        <AvatarLabel name={names.current[n]} left={x} top={y} key={n} />
      ))}

      <EditButton onClick={createAvatar} />
    </SceneContainer>
  );
};

const mapStateToProps = (state: never) => ({
  data: getUserEntityData(state),
  campaign: getCampaign(state),
  user: getUserState(state)
});

export default connect(mapStateToProps)(Overview);
